import { ChangeDetectionStrategy, Component, computed, effect, inject } from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { SeSecurityStore } from "./se.security.store";
import { S25RouterLinkDirective } from "../s25-nav-bar/s25.router.link.directive";
import { StateStore } from "../../services/state/state.store";
import { S25States } from "../../services/state/state.map";
import { LoginStore } from "../../stores/login.store";
import { UserStore } from "../../stores/user.store";

type Connect25ConfigNavItem = {
    stateName: S25States.Name;
    label: string;
};

const CONNECT25_CONFIG_NAV: Connect25ConfigNavItem[] = [
    {
        stateName: S25States.names.seConnect25ConfigAlerts,
        label: "Alerts",
    },
    {
        stateName: S25States.names.seConnect25ConfigAppearance,
        label: "Appearance",
    },
    {
        stateName: S25States.names.seConnect25ConfigAuthentication,
        label: "Authentication",
    },
    {
        stateName: S25States.names.seConnect25ConfigImport,
        label: "Import Tool",
    },
    {
        stateName: S25States.names.seConnect25ConfigInit,
        label: "Initialization",
    },
    {
        stateName: S25States.names.seConnect25ConfigOrgStatus,
        label: "Org Status",
    },
];

@TypeManagerDecorator("s25-ng-se-config")
@Component({
    selector: "s25-ng-se-config",
    template: `
        <div class="c-padding--double se-config-shell">
            <h1>Connect25 Configuration</h1>
            <p class="se-config-shell__intro">
                Manage Connect25 initialization, appearance, authentication, alerts, and import tools.
            </p>

            @if (visibleNavItems().length) {
                <nav class="se-config-shell__nav" aria-label="Connect25 configuration sections">
                    @for (navItem of visibleNavItems(); track navItem.stateName) {
                        <a
                            s25-ng-router-link
                            [stateName]="navItem.stateName"
                            class="se-config-shell__nav-link"
                            [class.se-config-shell__nav-link--active]="isActiveNav(navItem.stateName)"
                        >
                            {{ navItem.label }}
                        </a>
                    }
                </nav>
            }
        </div>
    `,
    styles: `
        :host {
            display: block;
        }

        .se-config-shell__intro {
            margin-bottom: 1.5rem;
        }

        .se-config-shell__nav {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
            margin-bottom: 0.5rem;
        }

        .se-config-shell__nav-link {
            display: inline-block;
            padding: 0.5rem 1rem;
            border: 1px solid var(--color--border);
            border-radius: 0.25rem;
            color: var(--color--link-text);
            text-decoration: none;
            font-weight: 500;
            background: var(--color--surface-bg);

            &:hover,
            &:focus {
                color: var(--color--link-text-hover);
                border-color: var(--color--link-text);
            }
        }

        .se-config-shell__nav-link--active {
            color: var(--color--surface-bg);
            background: var(--color--link-text);
            border-color: var(--color--link-text);

            &:hover,
            &:focus {
                color: var(--color--surface-bg);
            }
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25RouterLinkDirective],
})
export class SeConfigComponent {
    protected readonly securityStore = inject(SeSecurityStore);
    private readonly stateStore = inject(StateStore);
    private readonly loginStore = inject(LoginStore);
    private readonly userStore = inject(UserStore);

    private readonly navVisibilityByState = computed((): Partial<Record<S25States.Name, boolean>> => {
        void this.securityStore.isInitialized();
        const hasFullFls = this.securityStore.hasFLSOverride();
        const canConfigureAlerts = this.securityStore.canConfigureSystemAlerts();
        const canConfigureOrgStatus = this.securityStore.canConfigureOrgStatus();
        const canSeAdmin = this.securityStore.canSeAdmin();
        const canImport = this.loginStore.loggedIn() && this.userStore.secGroupId() === -1;

        return {
            [S25States.names.seConnect25ConfigInit]: hasFullFls,
            [S25States.names.seConnect25ConfigAppearance]: hasFullFls,
            [S25States.names.seConnect25ConfigAuthentication]: canSeAdmin,
            [S25States.names.seConnect25ConfigAlerts]: canConfigureAlerts,
            [S25States.names.seConnect25ConfigImport]: canImport,
            [S25States.names.seConnect25ConfigOrgStatus]: canConfigureOrgStatus,
        };
    });

    readonly visibleNavItems = computed(() => {
        const visibilityByState = this.navVisibilityByState();
        return CONNECT25_CONFIG_NAV.filter((navItem) => visibilityByState[navItem.stateName]);
    });

    readonly currentStateName = computed(() => this.stateStore.currentStateName());

    constructor() {
        effect(() => {
            void this.securityStore.ensureInitialized();
        });
    }

    isActiveNav(stateName: S25States.Name): boolean {
        return this.currentStateName() === stateName;
    }
}
